import {
  ReservationSearchWithDetailsDocument,
  ReservationSearchWithDetailsQuery,
  ReservationSearchWithDetailsQueryVariables,
  ReservationSearchWithRRFragment,
  reservationSearchFragment,
} from 'src/api/api.generated'
import QueryLookup from '../query-lookup/query-lookup'
import { useHotelDate, useTenant } from 'src/app/hooks/context/context.hook'
import { Stack, Avatar, Typography, Box } from '@vapor/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBed } from '@fortawesome/pro-regular-svg-icons'
import useFormatDate from 'src/app/hooks/current-locale/use-format-date'
import useLocalizeString from 'src/app/hooks/current-locale/use-localized-string'
import { LocalizedString } from 'hic-components'

type ReservationWithReservedLookupProps = {
  propertyId: string
  value?: reservationSearchFragment | { id: string }
  valueType?: 'reservation' | 'reservedResource'
  onChange: (value: ReservationSearchWithRRFragment | null) => void
  includeReservedResources?: boolean
}
export const ReservationWithReservedLookup = (props: ReservationWithReservedLookupProps) => {
  const { propertyId, value, onChange } = props
  const { id: tenantId } = useTenant()
  const formatDate = useFormatDate()
  const hotelDate = useHotelDate()
  const localizeString = useLocalizeString()

  return (
    <>
      <QueryLookup<ReservationSearchWithRRFragment, ReservationSearchWithDetailsQuery, ReservationSearchWithDetailsQueryVariables> //
        value={value ? value : null}
        onChange={onChange}
        queryDocument={ReservationSearchWithDetailsDocument}
        fetchQueryVariables={(id) => ({
          filter: {
            id: id,
            tenantId: tenantId,
            propertyId: propertyId,
          },
          limit: 1,
        })}
        getFetchRecord={(data): ReservationSearchWithRRFragment | null => {
          const reservation = data.reservationSearch?.[0]
          if (reservation) {
            if (props.valueType === 'reservedResource' && value?.id) {
              const reservedResource = reservation.reservedResources?.find((rr) => rr.id === value.id)
              if (reservedResource) return { ...reservation, reservedResources: [reservedResource] }
            }
            return reservation
          }
          return null
        }}
        searchQueryVariables={(text) => ({
          filter: {
            tenantId: tenantId,
            propertyId: propertyId,
            search: text,
            filterType: 'DEPARTURE',
            filterFrom: hotelDate,
          },
        })}
        getSearchRecords={(data: ReservationSearchWithDetailsQuery) => {
          const result =
            data.reservationSearch?.reduce((acc: ReservationSearchWithRRFragment[], curr) => {
              const commonReservationData = { ...curr, reservedResources: undefined }
              const flatternReservedResources =
                curr.reservedResources?.map<ReservationSearchWithRRFragment>((rr) => ({ ...commonReservationData, id: `${commonReservationData.id}#${rr.id}`, reservedResources: [rr] })) ?? []
              return [...acc, commonReservationData, ...flatternReservedResources]
            }, []) || []
          return result
        }}
        getOptionLabel={(option) => {
          const reservedResourceData = option.reservedResources?.[0]
          if (reservedResourceData) {
            // Reserved Resource
            const { resourceType, resource } = getReservedResourceData(reservedResourceData, localizeString)
            return `${option.reservationCode} - ${resourceType} ${resource ? `${resource.code}` : ''} `
          } else {
            //Reservation
            return `${option.reservationCode} - ${option.company ?? option.mainGuest ?? ''}`
          }
        }}
        renderOption={(value) => {
          if (!value.reservedResources) {
            return (
              <Stack direction="row" alignItems="center" spacing={1} width="100%" minWidth={{ xs: '100%', sm: '350px', md: '450px' }}>
                <Avatar>
                  <FontAwesomeIcon icon={faBed} style={{ width: '18px', height: '18px', color: 'var(--Cadet)' }} />
                </Avatar>
                <Stack key={value.id} direction="column" spacing={0.5}>
                  <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={0.5}>
                    <Typography variant="caption" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <b>{`${value.reservationCode} - ${value.company ? `${value.company} (${value.mainGuest})` : value.mainGuest}`}</b>
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    <Typography variant="caption">{`${formatDate(value.start)} > ${formatDate(value.end)}`}</Typography>
                  </Stack>
                </Stack>
              </Stack>
            )
          } else {
            return <ReservedResourceOption value={value} />
          }
        }}
      />
    </>
  )
}

const ReservedResourceOption = ({ value }: { value: ReservationSearchWithRRFragment }) => {
  const localizeString = useLocalizeString()

  const reservedResourceData = value.reservedResources?.[0]

  if (reservedResourceData) {
    const { guest, resourceType, resource } = getReservedResourceData(reservedResourceData, localizeString)
    const guestOrAlternative = guest ?? value.company ?? value.mainGuest ?? undefined

    return (
      <Stack direction="row" alignItems="center" spacing={1} width="100%" minWidth={{ xs: '100%', sm: '350px', md: '450px' }}>
        <Box sx={{ width: 40, display: 'block' }} />

        <Stack key={value.id} direction="column" spacing={0.5}>
          <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={0.5}>
            <Typography variant="caption" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <b>{`${resourceType} ${resource ? `- ${resource.code}` : ''}`}</b>
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Typography variant="caption">{`${guestOrAlternative ?? ''}`}</Typography>
          </Stack>
        </Stack>
      </Stack>
    )
  } else {
    return <span>ND</span>
  }
}

const getReservedResourceData = (
  data: NonNullable<NonNullable<Pick<ReservationSearchWithRRFragment, 'reservedResources'>>['reservedResources']>[number],
  localizeString: (localizedString: LocalizedString | null | undefined) => string,
) => {
  const guest = data.guestOccupancies?.length ? data.guestOccupancies?.[data.guestOccupancies?.length - 1].guest.name : undefined
  const { assignedResourceType, resource } = data.resourceOccupancies[data.resourceOccupancies.length - 1]
  const resourceType = assignedResourceType?.code ?? localizeString(assignedResourceType.customName) ?? localizeString(assignedResourceType.name.description)

  return { guest, resourceType, resource }
}
